import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Building2, MapPin, FileText, Save, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { OuvidoriaCreationService } from '@/services/ouvidoriaCreationService';
import { AppConfig } from '@/datasources/AppConfig';
import { Checkbox } from '@/components/ui/checkbox';
import { elegivelAssociacaoSupervisora } from '@/lib/ouvidoria';
// Switch removido - campo geral não é mais exposto na UI

interface EnderecoOuvidoria {
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  complemento: string;
  cep: string;
  pais: string;
  ibge: string;
  latitude?: number;
  longitude?: number;
}

interface DadosCNPJ {
  nome: string;
  fantasia: string;
  razao_social: string;
  numero: string;
  cnpj: string;
  situacao: string;
  natureza_juridica: string;
  atividade_principal: Array<{ code: string; text: string }>;
  cep: string;
}

interface DadosAgente {
  nome: string;
  email: string;
  telefone: string;
}

interface OuvidoriaData {
  dadosCNPJ: DadosCNPJ;
  endereco: EnderecoOuvidoria;
  municipio: { id: string; nome: string; municipioLong: number; codigoIBGE: string };
  cnpj: string;
  geral?: boolean;
  isAssociacaoPrivada?: boolean;
  tokenId?: string | null;
}

type ModoCadastro = 'self' | 'admin';

export default function EditarOuvidoriaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const modoCadastro: ModoCadastro = (location.state as { modoCadastro?: ModoCadastro } | null)?.modoCadastro === 'admin' ? 'admin' : 'self';
  const isAdmin = modoCadastro === 'admin';
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    razaoSocial: '',
    nomeFantasia: '',
    naturezaJuridica: '',
    atividadePrincipal: '',
    email: '',
    telefone: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    pais: 'Brasil',
    ibge: '',
    geral: false,
    associacaoSupervisora: false,
    nomeAgente: '',
    emailAgente: '',
    telefoneAgente: '',
  });
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [cnpjValue, setCnpjValue] = useState('');
  const [isAssociacaoPrivada, setIsAssociacaoPrivada] = useState(false);
  const [municipioData, setMunicipioData] = useState<{ id: string; nome: string; municipioLong: number; codigoIBGE: string } | null>(null);

  useEffect(() => {
    const state = location.state as OuvidoriaData;
    if (!state || !state.dadosCNPJ || !state.endereco || !state.municipio) {
      toast({
        title: 'Erro',
        description: 'Dados não encontrados. Por favor, valide o CNPJ novamente.',
        variant: 'destructive',
      });
      navigate(-1);
      return;
    }

    setMunicipioData({
      id: state.municipio.id,
      nome: state.municipio.nome,
      municipioLong: state.municipio.municipioLong,
      codigoIBGE: state.municipio.codigoIBGE || '',
    });
    setFormData({
      nome: state.dadosCNPJ.nome || '',
      cnpj: state.dadosCNPJ.cnpj || '',
      razaoSocial: state.dadosCNPJ.razao_social || '',
      nomeFantasia: state.dadosCNPJ.fantasia || '',
      naturezaJuridica: state.dadosCNPJ.natureza_juridica || '',
      atividadePrincipal: state.dadosCNPJ.atividade_principal[0]?.text || '',
      email: '',
      telefone: '',
      logradouro: state.endereco.logradouro || '',
      numero: state.endereco.numero || '',
      complemento: state.endereco.complemento || '',
      bairro: state.endereco.bairro || '',
      cidade: state.endereco.cidade || '',
      estado: state.endereco.estado || '',
      cep: state.endereco.cep || '',
      pais: state.endereco.pais || 'Brasil',
      ibge: state.endereco.ibge || '',
      geral: state.geral ?? false,
      associacaoSupervisora: false,
      nomeAgente: '',
      emailAgente: '',
      telefoneAgente: '',
    });
    setCnpjValue(state.cnpj || '');
    setTokenId(state.tokenId ?? null);
    setIsAssociacaoPrivada(
      state.isAssociacaoPrivada ??
        (state.dadosCNPJ.natureza_juridica || '').includes('Associação Privada'),
    );
  }, [location.state, navigate, toast]);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (!municipioData) {
        throw new Error('Dados do município não encontrados');
      }

      if (formData.geral && (!formData.nomeAgente || !formData.emailAgente)) {
        toast({
          title: 'Campos obrigatórios',
          description: 'Preencha o nome e e-mail do agente gestor.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Associação supervisora exige natureza "Associação Privada" e atividade
      // principal de defesa de direitos sociais (CNAE 9430-8/00) — regra do backend.
      if (
        formData.associacaoSupervisora &&
        !elegivelAssociacaoSupervisora(
          formData.naturezaJuridica,
          formData.atividadePrincipal,
        )
      ) {
        toast({
          title: 'Associação supervisora inelegível',
          description:
            'Exige natureza jurídica "Associação Privada" e atividade principal de associações de defesa de direitos sociais (CNAE 9430-8/00).',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const ouvidoriaData = {
        nome: formData.nome,
        cnpj: formData.cnpj,
        razaoSocial: formData.razaoSocial,
        latitude: location.state?.endereco?.latitude,
        longitude: location.state?.endereco?.longitude,
        naturezaJuridica: formData.naturezaJuridica,
        atividadePrincipal: formData.atividadePrincipal,
        associacaoSupervisora: formData.associacaoSupervisora,
        nomeFantasia: formData.nomeFantasia,
        cidade: municipioData?.nome || formData.cidade,
        geral: formData.geral,
        email: formData.email,
        telefone: formData.telefone,
        endereco: {
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
          cep: formData.cep,
          pais: formData.pais,
          ibge: municipioData?.codigoIBGE || formData.ibge || '',
          latitude: location.state?.endereco?.latitude,
          longitude: location.state?.endereco?.longitude,
        },
      };

      const dadosAgente: DadosAgente | undefined = formData.geral ? {
        nome: formData.nomeAgente,
        email: formData.emailAgente,
        telefone: formData.telefoneAgente,
      } : undefined;

      navigate('/configurar-categorias', {
        state: {
          dadosCNPJ: location.state?.dadosCNPJ,
          endereco: location.state?.endereco,
          municipio: municipioData,
          ouvidoriaData: ouvidoriaData,
          modoCadastro,
          dadosAgente,
          tokenId,
        },
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Ocorreu um erro ao salvar a ouvidoria.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Editar Dados da Ouvidoria</h1>
            <p className="text-muted-foreground mt-2">
              Revise e edite as informações da ouvidoria antes de confirmar o cadastro.
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Dados da Empresa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>
                Informações cadastrais da instituição
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={handleInputChange('cnpj')}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="razaoSocial">Razão Social *</Label>
                  <Input
                    id="razaoSocial"
                    value={formData.razaoSocial}
                    onChange={handleInputChange('razaoSocial')}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nomeFantasia">Nome Fantasia</Label>
                  <Input
                    id="nomeFantasia"
                    value={formData.nomeFantasia}
                    onChange={handleInputChange('nomeFantasia')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="naturezaJuridica">Natureza Jurídica</Label>
                  <Input
                    id="naturezaJuridica"
                    value={formData.naturezaJuridica}
                    onChange={handleInputChange('naturezaJuridica')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="atividadePrincipal">Atividade Principal</Label>
                  <Input
                    id="atividadePrincipal"
                    value={formData.atividadePrincipal}
                    onChange={handleInputChange('atividadePrincipal')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    type="tel"
                    value={formData.telefone}
                    onChange={handleInputChange('telefone')}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              {/* Associação Supervisora — apenas para Associação Privada não-geral */}
              {isAssociacaoPrivada && !formData.geral && (
                <div className="flex items-start space-x-3 pt-2 border-t">
                  <Checkbox
                    id="associacaoSupervisora"
                    checked={formData.associacaoSupervisora}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        associacaoSupervisora: checked === true,
                      }))
                    }
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="associacaoSupervisora"
                      className="font-medium cursor-pointer"
                    >
                      Associação Supervisora
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Marque se esta associação supervisiona as demais associações
                      de moradores do município. Exige atividade principal de
                      defesa de direitos sociais (CNAE 9430-8/00).
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Endereço
              </CardTitle>
              <CardDescription>
                Informações de localização da ouvidoria
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="logradouro">Logradouro *</Label>
                  <Input
                    id="logradouro"
                    value={formData.logradouro}
                    onChange={handleInputChange('logradouro')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="numero">Número *</Label>
                  <Input
                    id="numero"
                    value={formData.numero}
                    onChange={handleInputChange('numero')}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={formData.complemento}
                    onChange={handleInputChange('complemento')}
                    placeholder="Apto, Bloco, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro *</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange('bairro')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange('cidade')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estado">Estado *</Label>
                  <Input
                    id="estado"
                    value={formData.estado}
                    onChange={handleInputChange('estado')}
                    maxLength={2}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <Input
                    id="cep"
                    value={formatCEP(formData.cep)}
                    onChange={(e) => {
                      const numbers = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, cep: numbers }));
                    }}
                    maxLength={9}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pais">País</Label>
                  <Input
                    id="pais"
                    value={formData.pais}
                    onChange={handleInputChange('pais')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ibge">Código IBGE</Label>
                  <Input
                    id="ibge"
                    value={formData.ibge}
                    onChange={handleInputChange('ibge')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dados do Agente Gestor */}
          {formData.geral && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCircle className="h-5 w-5" />
                  Dados do Agente Gestor
                </CardTitle>
                <CardDescription>
                  Informações do gestor que administrará a ouvidoria
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="nomeAgente">Nome do Gestor *</Label>
                    <Input
                      id="nomeAgente"
                      value={formData.nomeAgente}
                      onChange={handleInputChange('nomeAgente')}
                      placeholder="Nome completo do gestor"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailAgente">E-mail do Gestor *</Label>
                    <Input
                      id="emailAgente"
                      type="email"
                      value={formData.emailAgente}
                      onChange={handleInputChange('emailAgente')}
                      placeholder="gestor@exemplo.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefoneAgente">Telefone do Gestor</Label>
                    <Input
                      id="telefoneAgente"
                      type="tel"
                      value={formData.telefoneAgente}
                      onChange={handleInputChange('telefoneAgente')}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Criar Ouvidoria
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
